import { Decimal, Money } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import { datefromDateFormat } from "services/utility/dates";
import { handleProgramError } from "services/utility/errors";
import {
  formatMoneyWithAdornments,
  formatMoneyWithoutAdornments,
  formatDecimal,
} from "services/utility/formatting";

import {
  CUSTOM_REPORT,
  STANDARD_REPORT,
  SCHEDULED_REPORT_OPTIONS,
} from "views/Reports/reportConstants";
import { initialErrorState } from "views/common";

import globalState from "globalState/globalState";

import { getObjectFromReportString } from "appConstants";
import { format } from "date-fns";

const ROW_EXTRACTOR = /<tr.*?<\/tr>/g;
const INDEX_VIEW_FINDER = /href="(\/(.*?)\/IndexView\/(\d+))"/;

const NO_VENDOR = i18n("carat.noVendor");
const NO_CATEGORY = i18n("carat.noCategory");

export function processTbody(rawTbody) {
  // get rid of newlines
  const bodyNoNewlines = rawTbody.replace(/\r/g, "");

  // break into individual tr's
  const trMatches = [...bodyNoNewlines.matchAll(ROW_EXTRACTOR)];
  const trs = trMatches.map((trMatch) => trMatch[0]);

  const processedTrs = trs.map((tr) => {
    // change any anchor tags
    // 1. remove the target attribute
    // 2. change the href to the desired route
    //    before: <a target="_blank" href="/Item/IndexView/2229">
    //    after:  <a href="item/2229">
    let trWithoutTarget = tr.replace(' target="_blank"', "");

    while (trWithoutTarget.match("IndexView")) {
      const matches = trWithoutTarget.match(INDEX_VIEW_FINDER);
      const originalLink = matches[1];
      const reportObjectType = matches[2];
      const objectId = matches[3];
      const objectType = getObjectFromReportString(reportObjectType).fullString;
      trWithoutTarget = trWithoutTarget.replace(
        originalLink,
        `/${objectType}?id=${objectId}`
      );
    }

    return trWithoutTarget;
  });
  return processedTrs.join("");
}

export function processThead(rawThead, columns) {
  let newThead = rawThead;
  columns.forEach(({ apiName, label, number, noSort, date }) => {
    if (date) {
      newThead = newThead.replace(
        `<th>${apiName}</th>`,
        `<th class="tsj-dates">${apiName}</th>`
      );
      // TODO: remove this additional tsj-numbers replace
      // once https://app.clickup.com/t/868ddqgww complete
      newThead = newThead.replace(
        `<th class="tsj-numbers">${apiName}</th>`,
        `<th class="tsj-dates">${apiName}</th>`
      );
    } else if (number) {
      newThead = newThead.replace(
        `<th>${apiName}</th>`,
        `<th class="tsj-numbers">${apiName}</th>`
      );
    } else if (noSort) {
      newThead = newThead.replace(
        `<th>${apiName}</th>`,
        `<th class="disable-sort">${apiName}</th>`
      );
    }

    if (label) {
      newThead = newThead.replace(`>${apiName}</`, `>${label}</`);
    }
  });
  return newThead;
}

const mappedIdValues = {
  vendor: "vendorId",
  customer: "customerId",
  customerType: "customerTypeId",
  item: "itemId",
  class: "classId",
  department: "departmentId",
  channel: "channelId",
  priceGroup: "priceGroupId",
};

export function formatSettingsForApi(
  settingFilters,
  settings,
  allFilterChoicesSelected,
  includePaymentDates
) {
  const {
    categories,
    locations,
    item,
    date1,
    date2,
    users,
    salesReps,
    date1Macro,
    date2Macro,
    valueCalculation,
    groupBy,
    orderType,
    paymentDate1Macro,
    paymentDate2Macro,
    txnType,
  } = settings;

  const includeTotalOnlyOption = settingFilters.find(
    ({ apiName }) => apiName === "locations"
  )?.includeTotalOnlyOption;

  const locationsDefault = [includeTotalOnlyOption ? -1 : 0];
  // convert our filter values to what's expected on the back end
  const apiSettings = {
    ...settings,
    dateFormat: "yyyy-MM-dd",
    categories:
      categories?.length && !allFilterChoicesSelected.categories
        ? categories.map(({ id }) => id)
        : [0],
    locations: locations?.length
      ? locations.map(({ id }) => id)
      : locationsDefault,
    users: users ? users.map(({ id }) => id) : [null],
    salesReps: salesReps ? salesReps.map(({ id }) => id) : [null],
    date1: date1 ? format(date1, "yyyy-MM-dd") : null,
    date2: date2 ? format(date2, "yyyy-MM-dd") : null,
    date1Macro: date1Macro ? date1Macro.id : null,
    date2Macro: date2Macro ? date2Macro.id : null,
    paymentDate1Macro: paymentDate1Macro?.id || null,
    paymentDate2Macro: paymentDate2Macro?.id || null,
    valueCalculation: valueCalculation?.id || null,
    txnType: txnType?.id || null,
    groupBy: groupBy?.id || null,
    orderType: orderType?.id || null,
    // itemname appears to be used on the back end to create the spreadsheet
    // name on (only) the Lot Traceability and Expanded BOM reports
    itemName: item?.name || null,
  };

  // vendors, items, and customers use xxxxxxId in
  // the API, but in v8 it was just xxxxxxx
  Object.keys(mappedIdValues).forEach((objectType) => {
    if (apiSettings.hasOwnProperty(objectType)) {
      const idKey = mappedIdValues[objectType];
      apiSettings[idKey] = apiSettings[objectType]?.id || 0;
      delete apiSettings[objectType];
    }
  });

  const paymentsBoarded =
    globalState.getState().userCompanySettings.settings.paymentsBoarded;

  // for reports that include payment data options,
  // only include these params when the account
  // is payments boarded, and the user has opted
  // to only show the corresponding data that
  // has been invoiced or paid
  if (paymentsBoarded && settings.showFullOnly && includePaymentDates) {
    apiSettings.paymentDate1 = apiSettings.paymentDate1
      ? format(apiSettings.paymentDate1, "yyyy-MM-dd")
      : null;
    apiSettings.paymentDate2 = apiSettings.paymentDate2
      ? format(apiSettings.paymentDate2, "yyyy-MM-dd")
      : null;
  } else {
    apiSettings.paymentDate1 = null;
    apiSettings.paymentDate2 = null;
    apiSettings.paymentDate1Macro = null;
    apiSettings.paymentDate2Macro = null;
  }

  return apiSettings;
}

/**
 * @name    getFavoriteId
 *
 * @summary see if a report has been favorited by the current user
 *
 * @param   favorites (array) - list of favorited reports
 *
 * @param   reportCategory (string) - STANDARD_REPORT, CUSTOM_REPORT, or
 *          SCHEDULED_REPORT
 *
 * @param   reportIdentifier (string) - either the custom/scheduled report
 *          id or the reportType string
 *
 * @returns returns the id of the report favorite record, or null if
 *          this report is not a favorite of this user
 */
export function getFavoriteId(favorites, reportCategory, reportIdentifier) {
  const favoriteRecord = favorites.find((favorite) => {
    if (reportCategory === STANDARD_REPORT) {
      return favorite.type === reportIdentifier;
    }
    if (reportCategory === CUSTOM_REPORT) {
      return favorite.reportCustomizationId === reportIdentifier;
    }
    handleProgramError(
      new Error(
        `getFavoriteId | unexpected value of ${reportCategory} for reportCategory`
      )
    );
    return false;
  });
  return favoriteRecord ? favoriteRecord.id : null;
}

function sortDates(a, b, dateFormat) {
  const [aSplit] = a.split("#");
  const [bSplit] = b.split("#");
  const dateA = aSplit ? datefromDateFormat(aSplit, dateFormat) : null;
  const dateB = bSplit ? datefromDateFormat(bSplit, dateFormat) : null;
  return dateA - dateB;
}

/**
 * @name    tableSortJs
 *
 * @summary make a displayed (HTML) report table sortable
 *
 * @license This function is based heavily on the table-sort-js package
 *          written by Lee Wannacott
 *          Licence: MIT License Copyright (c) 2021 Lee Wannacott
 *          GitHub Repository: https://github.com/LeeWannacott/table-sort-js
 *          npm package: https://www.npmjs.com/package/table-sort-js
 *
 * @param   testingTableSortJS (boolean) -  if true, runs this function
 *          in a manner compatible with a testing environment
 *
 * @param   domDocumentWindow (object) - reference to the DOM windown
 *          object in which tables should be made sortable
 *
 * @returns null
 */

// we don't use arrows, but leaving this in for compatibility with original
// table-sort-js
const ARROW_UP = " ▲";
const ARROW_DOWN = " ▼";

export function tableSortJs(tableToSort) {
  const columnIndexAndTableRow = {};

  makeTableSortable(tableToSort);

  function makeTableSortable(sortableTable) {
    let createTableHead;
    let tableBody;
    if (sortableTable.getElementsByTagName("thead").length === 0) {
      createTableHead = document.createElement("thead");
      createTableHead.appendChild(sortableTable.rows[0]);
      sortableTable.insertBefore(createTableHead, sortableTable.firstChild);
      if (sortableTable.querySelectorAll("tbody").length > 1) {
        tableBody = sortableTable.querySelectorAll("tbody")[1];
      } else {
        tableBody = sortableTable.querySelector("tbody");
      }
    } else {
      tableBody = sortableTable.querySelector("tbody");
    }

    const tableHead = sortableTable.querySelector("thead");
    const tableHeadHeaders = tableHead.querySelectorAll("th");

    for (let [columnIndex, th] of tableHeadHeaders.entries()) {
      if (!th.classList.contains("disable-sort")) {
        th.style.cursor = "pointer";
        makeEachColumnSortable(th, columnIndex, tableBody, sortableTable);
      }
    }
  }

  function makeEachColumnSortable(th, columnIndex, tableBody, sortableTable) {
    let desc = th.classList.contains("order-by-desc");
    let tableArrows = sortableTable.classList.contains("table-arrows");

    if (desc && tableArrows) {
      th.insertAdjacentText("beforeend", ARROW_DOWN);
    } else if (tableArrows) {
      th.insertAdjacentText("beforeend", ARROW_UP);
    }

    function sortDataAttributes(tableRows, columnData) {
      for (let [i, tr] of tableRows.entries()) {
        const dataAttributeTd = tr.querySelectorAll("td").item(columnIndex)
          .dataset.sort;
        columnData.push(`${dataAttributeTd}#${i}`);
        columnIndexAndTableRow[columnData[i]] = tr.innerHTML;
      }
    }

    let timesClickedColumn = 0;
    let columnIndexesClicked = [];

    function rememberSort(timesClickedColumn, columnIndexesClicked) {
      // Check if user has clicked different column from the first column if
      // yes reset times clicked.
      columnIndexesClicked.push(columnIndex);
      if (timesClickedColumn === 1 && columnIndexesClicked.length > 1) {
        const lastColumnClicked =
          columnIndexesClicked[columnIndexesClicked.length - 1];
        const secondLastColumnClicked =
          columnIndexesClicked[columnIndexesClicked.length - 2];
        if (lastColumnClicked !== secondLastColumnClicked) {
          timesClickedColumn = 0;
          columnIndexesClicked.shift();
        }
      }
    }

    function getColSpanData(sortableTable, colSpanData, colSpanSum) {
      sortableTable.querySelectorAll("th").forEach((th, index) => {
        colSpanData[index] = th.colSpan;
        if (index === 0) colSpanSum[index] = th.colSpan;
        else colSpanSum[index] = colSpanSum[index - 1] + th.colSpan;
      });
    }

    function getTableData(
      tableRows,
      columnData,
      isDataAttribute,
      colSpanData,
      colSpanSum
    ) {
      for (let [i, tr] of tableRows.entries()) {
        // inner text for column we click on
        let tdTextContent = tr
          .querySelectorAll("td")
          .item(
            colSpanData[columnIndex] === 1
              ? colSpanSum[columnIndex] - 1
              : colSpanSum[columnIndex] - colSpanData[columnIndex]
          ).textContent;

        if (tdTextContent.length === 0) {
          tdTextContent = "";
        }
        if (!isDataAttribute) {
          columnData.push(`${tdTextContent}#${i}`);
          columnIndexAndTableRow[`${tdTextContent}#${i}`] = tr.innerHTML;
        }
      }

      function normalizeNumericString(rawNumberString) {
        // get rid of index stuff used elsewhere by this library
        const [suffixRemoved] = rawNumberString.split("#");

        // check if number is wrapped in parentheses (accounting-style negative)
        const isNegative = /^\s*\(.*\)\s*$/.test(suffixRemoved);

        // Remove all characters except digits, commas, dots, minus, and spaces
        let cleaned = suffixRemoved.replace(/[^\d,.-]/g, "");

        // handle European-style: if comma is decimal and dot is thousand
        const commaIndex = cleaned.lastIndexOf(",");
        const dotIndex = cleaned.lastIndexOf(".");

        const normalizedNumber =
          commaIndex > dotIndex
            ? cleaned.replace(/\./g, "").replace(",", ".") // "111,000,111.12" → "111000111.12"
            : cleaned.replace(/,/g, ""); // "1.234.567,89" → "1234567.89"

        return isNegative ? -normalizedNumber : normalizedNumber;
      }

      function isMeaningful(value) {
        const [suffixRemoved] = value.split("#");
        return suffixRemoved.trim() !== "";
      }

      function naturalSortAscending(a, b) {
        if (th.classList.contains("tsj-numbers")) {
          const cleanedA = isMeaningful(a)
            ? Number(normalizeNumericString(a))
            : Number.NEGATIVE_INFINITY;
          const cleanedB = isMeaningful(b)
            ? Number(normalizeNumericString(b))
            : Number.NEGATIVE_INFINITY;
          return cleanedA < cleanedB ? -1 : 1;
        }
        if (th.classList.contains("tsj-dates")) {
          const { dateFormat, defaultDateFormat } =
            globalState.getState().userCompanySettings.settings;
          return sortDates(a, b, dateFormat || defaultDateFormat);
        }
        return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
      }

      function naturalSortDescending(a, b) {
        return naturalSortAscending(b, a);
      }

      function clearArrows() {
        th.innerText = th.innerText.replace(ARROW_UP, "");
        th.innerText = th.innerText.replace(ARROW_DOWN, "");
      }

      // Sort naturally; default ascending unless th contains 'order-by-desc'
      // as className.
      if (columnData[0] === undefined) {
        return;
      }

      if (timesClickedColumn === 1) {
        if (desc) {
          if (tableArrows) {
            clearArrows();
            th.insertAdjacentText("beforeend", ARROW_DOWN);
          }
          columnData.sort(naturalSortDescending);
        } else {
          if (tableArrows) {
            clearArrows();
            th.insertAdjacentText("beforeend", ARROW_UP);
          }
          columnData.sort(naturalSortAscending);
        }
      } else if (timesClickedColumn === 2) {
        timesClickedColumn = 0;
        if (desc) {
          if (tableArrows) {
            clearArrows();
            th.insertAdjacentText("beforeend", ARROW_UP);
          }
          columnData.sort(naturalSortAscending);
        } else {
          if (tableArrows) {
            clearArrows();
            th.insertAdjacentText("beforeend", ARROW_DOWN);
          }
          columnData.sort(naturalSortDescending);
        }
      }
    }

    function updateTable(tableRows, columnData) {
      for (let [i, tr] of tableRows.entries()) {
        tr.innerHTML = columnIndexAndTableRow[columnData[i]];
      }
    }

    th.addEventListener("click", function () {
      const columnData = [];
      const colSpanData = {};
      const colSpanSum = {};

      const visibleTableRows = Array.prototype.filter.call(
        tableBody.querySelectorAll("tr"),
        (tr) => {
          return tr.style.display !== "none";
        }
      );

      // we're not currently using "data-sort", but leaving it in in
      // case we want to use it in the future
      let isDataAttribute = th.classList.contains("data-sort");
      if (isDataAttribute) {
        sortDataAttributes(visibleTableRows, columnData);
      }

      // we're not currently using "remember-sort", but leaving it in in
      // case we want to use it in the future
      let isRememberSort = sortableTable.classList.contains("remember-sort");
      if (!isRememberSort) {
        rememberSort(timesClickedColumn, columnIndexesClicked);
      }

      timesClickedColumn += 1;

      getColSpanData(sortableTable, colSpanData, colSpanSum);
      getTableData(
        visibleTableRows,
        columnData,
        isDataAttribute,
        colSpanData,
        colSpanSum
      );

      // set class on clicked header cell so it can be styled
      // and remove it from other header cells that might have previously
      // been sorted
      sortableTable
        .querySelectorAll("th.current-sort")
        .forEach((el) => el.classList.remove("current-sort"));
      this.classList.add("current-sort");

      updateTable(visibleTableRows, columnData);
    });
  }
}

/**
 * @name    isRetrievalDisabled
 *
 * @summary given an array of filters and a filter state object, this function
 *          determines whether any of the required filters do not have a value within
 *          the filter state object
 *
 * @param   filters (array)
 *
 * @param   filterState (object)
 *
 *
 * @returns a boolean indicating whether a specific report should have it's
 *          retrieval disabled or not
 */
export function isRetrievalDisabled(filters, filterState) {
  return filters.reduce((seed, { required, apiName }) => {
    // if seed has already been changed to true, or the filter is not required
    // return seed
    if (seed || !required) {
      return seed;
    }
    // if filter value is an empty array, then return true
    if (Array.isArray(filterState[apiName]) && !filterState[apiName].length) {
      return true;
    }
    // if filter value is null or undefined, then return falsey,
    // otherwise return true
    return !filterState[apiName];
  }, false);
}

/**
 * @name    extractSettingsValues
 *
 * @summary Given an array of report options or filters, it will
 *          return the default values
 *
 * @param   data (array) - the report filter or report option array
 *
 * @returns (object) with the default state that can be used
 *          to set the initial state or "clear" the chosen values
 *
 **/
export function extractSettingsValues(
  settings,
  reportCategory,
  customSettingsValues
) {
  return settings.reduce(
    (seed, { apiName, defaultValue }) => ({
      ...seed,
      [apiName]:
        // if it's a custom report, get the saved values from the API...
        reportCategory === CUSTOM_REPORT
          ? customSettingsValues[apiName]
          : // otherwise, it's a standard report, use the default in the
          // report definition
          typeof defaultValue === "function"
          ? defaultValue()
          : defaultValue,
    }),
    {}
  );
}

export function formatSchedule(schedule) {
  const { frequency, timeOfDay, dayOfWeek } = schedule;
  let parenthetical = "";
  if (frequency === "Daily") {
    parenthetical = ` at ${SCHEDULED_REPORT_OPTIONS.TIMES[
      timeOfDay
    ].name.toLowerCase()}`;
  }
  if (frequency === "Weekly") {
    parenthetical = ` on ${dayOfWeek}`;
  }
  return " " + frequency?.toLowerCase() + (parenthetical && `${parenthetical}`);
}

export function isValidSchedule(schedule) {
  const errors = initialErrorState();
  errors.entity = [];

  if (!schedule?.frequency) {
    errors.messages.push(i18n("reports.Alert.Scheduling.NoFrequency"));
    errors.entity.push("frequency");
  }
  if (schedule?.frequency === "Daily" && !schedule?.timeOfDay) {
    errors.messages.push(i18n("reports.Alert.Scheduling.MissingTimeOfDay"));
    errors.entity.push("timeOfDay");
  }
  if (schedule?.frequency === "Weekly" && !schedule?.dayOfWeek) {
    errors.messages.push(i18n("reports.Alert.Scheduling.MissingDayOfWeek"));
    errors.entity.push("dayOfWeek");
  }
  if (!schedule?.email && !schedule?.ftp) {
    errors.messages.push(i18n("reports.Alert.Scheduling.MissingEmailOrFtp"));
  }
  return errors;
}

export function getReportDecimalTotal(reportJson, column) {
  return formatDecimal(
    reportJson.reduce(
      (seed, row) =>
        row[column] && !row.isGroupByTotal ? seed.plus(row[column]) : seed,
      new Decimal(0)
    )
  );
}

export function getReportMoneyTotal(reportJson, column) {
  return formatMoneyWithAdornments(
    reportJson.reduce(
      (seed, row) => (!row.isGroupByTotal ? seed.plus(row[column]) : seed),
      new Money(0)
    )
  );
}

export function formatAccountingExceptionMoney(value, row) {
  const { exceptionCode } = row;
  switch (exceptionCode) {
    case "MatchingNotSentToQBO":
    case "CouldNotFindMatchinginQBO":
      return "";
    default:
      return formatMoneyWithoutAdornments(value);
  }
}

function getGroupByName(row, grouping) {
  const { vendor, category } = row;
  switch (grouping) {
    case "Vendor":
      return vendor?.name || NO_VENDOR;
    case "Category":
      return category?.name || NO_CATEGORY;
    default:
      throw new Error(`Group by ${grouping} not supported in getGroupByName`);
  }
}

export function groupAndSortJson(data, columns, grouping) {
  // group the data rows into an an array of objects containing
  // their respective group by name key (i.e. vendor, category, etc)
  // and a data key with the rows that correspond to that group by name
  const groupData = data.reduce((seed, row) => {
    const groupByName = getGroupByName(row, grouping);
    const foundInSeed = seed.find(({ groupName }) => groupName === groupByName);
    if (!foundInSeed) {
      // if not in seed, add groupName to it
      seed.push({ groupName: groupByName, data: [row] });
    } else {
      // if groupName in seed, add this row to its data
      seed.forEach(({ groupName, data }) => {
        if (groupName === groupByName) {
          data.push(row);
        }
      });
    }
    return seed;
  }, []);

  // sort groupData by the groupName attribute
  const sortedGroupData = groupData.sort((a, b) =>
    a.groupName < b.groupName ? -1 : 1
  );

  // flatten the groupName rows and the data rows into an array
  // so that the UI can render an ordered list
  const finalData = sortedGroupData.reduce((seed, row) => {
    const { groupName, data } = row;

    // add the grouping row and grouping total rows to the flattened array
    const groupingRow = { groupName };
    const groupingRowTotal = {
      groupName: `${groupName} ${i18n("global.Total")}`,
      isGroupByTotal: true,
    };

    // add totals for each group by total row for each column with "footerFunc" functionality
    columns.forEach(({ footerFunc, jsonName }) => {
      groupingRowTotal[jsonName] = footerFunc ? footerFunc(data, jsonName) : "";
    });
    return seed.concat([groupingRow, ...data, groupingRowTotal]);
  }, []);
  return finalData;
}
